import React, { useState } from 'react';
import cache from 'lscache';
import VpChevronUpIcon from '@vtmn-play/icons/react/VpChevronUpIcon';
import VpChevronDownIcon from '@vtmn-play/icons/react/VpChevronDownIcon';
import VpCheckIcon from '@vtmn-play/icons/react/VpCheckIcon';
import { VpAsset } from '@vtmn-play/react';
import { type VpAssetName } from '@vtmn-play/assets';
import { updateLanguage } from '@/store/userSlice';

import { useAppDispatch, useAppSelector } from '../../store/store';
import { USER_LANGUAGE_STORAGE_KEY } from './language-picker.constants';

import styles from './LanguagePicker.module.css';

export const getFlagName = (languageCode: string): VpAssetName => (languageCode === 'EN' ? 'flag-gb' : 'flag-fr');

export const CountryFlag = ({ languageCode }: { languageCode: string }) => (
  <div className={styles.countryFlag} data-testid="country-flag">
    <VpAsset name={getFlagName(languageCode)} width={28} />
  </div>
);

export const LanguagePicker = () => {
  const dispatch = useAppDispatch();
  const availableLanguages = useAppSelector(state => state.i18n.availableLanguages);
  const preferredLanguage = useAppSelector(state => state.user.preferredLanguage);
  const [displayFlags, setDisplayFlags] = useState(false);

  const languages = availableLanguages.map(({ language, locale }) => ({
    language,
    locale,
    selected: language === preferredLanguage,
  }));

  const switchLanguage = (language: string) => {
    cache.set(USER_LANGUAGE_STORAGE_KEY, language);
    dispatch(updateLanguage(language));
  };

  return (
    <div className={styles.languagePicker}>
      <button
        type="button"
        className={styles.languagePickerLabel}
        onClick={() => setDisplayFlags(prev => !prev)}
        aria-expanded={displayFlags}
        aria-haspopup="listbox"
        data-testid="language-picker-label"
      >
        <CountryFlag languageCode={preferredLanguage} />
        {displayFlags ? <VpChevronUpIcon data-testid="arrow-up" /> : <VpChevronDownIcon data-testid="arrow-down" />}
      </button>

      <div className={displayFlags ? styles.languageFlagsShow : styles.languageFlags} data-testid="language-flags">
        {languages.map(({ language, selected }) => (
          <button key={language} type="button" className={styles.flagChoice} onClick={() => switchLanguage(language)} data-testid="flag-choice">
            <CountryFlag languageCode={language} />
            {language}
            {selected && <VpCheckIcon />}
          </button>
        ))}
      </div>
    </div>
  );
};
